import React, { useEffect, useState, useCallback, memo } from 'react';
import { BackHandler, ScrollView, Image, SafeAreaView, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { connect, useSelector } from 'react-redux';
import Config from '../../libraries/ReactNativeConfig';
import Utility from '../../utils/Utility';
import List from '../List/List';
import { isAndroid, isBlank, isDesktop, isPresent } from '../../utils/BooleanUtility';
import {
  TabNavigationLeftHeader,
  TabNavigationRightHeader,
} from '../../components/header';
import { TABBAR_CONSTANT } from '../../config/Constants';
import colors from '../../theme/Colors';
import PayoffTabIcon from '../../components/shared/PayoffTabIcon';
import images from '../../theme/Images';
import { getStoreRef } from '../../store/StoreUtility';
import NavigationService from '../../navigator/NavigationService';
import BuildNumber from '../../components/shared/BuildNumber';

const MemoizedList = memo(List, (prevProps, nextProps) => {
  return (
    prevProps.slug === nextProps.slug &&
    prevProps.navigation === nextProps.navigation &&
    prevProps.previousScreen === nextProps.previousScreen
  );
});

const ForYouTabPage = ({ authToken = '', userSelfieData, userFacialAnalysisData }) => {
  const [listKey, setListKey] = useState(0);
  const [isOnboardingShown, setIsOnboardingShown] = useState(false);
  const navigation = useNavigation();

  useEffect(() => {
    setListKey((prev) => prev + 1);
  }, [authToken, userSelfieData, userFacialAnalysisData]);

  const handleBackButton = () => {
    navigation.navigate('Feed');
    return true;
  };

  useEffect(() => {
    if (!isAndroid()) return;
    BackHandler.addEventListener('hardwareBackPress', handleBackButton);
    return () => {
      BackHandler.removeEventListener('hardwareBackPress', handleBackButton);
    };
  }, [navigation]);

  const goBack = useCallback(() => {
    setIsOnboardingShown(false);
    navigation.navigate('Feed');
  }, [navigation]);

  const handleContinuePressed = useCallback(() => {
    const {
      profile: { name: profileName = '', email: profileEmail = '' },
    } = getStoreRef().getState().UserAccountInfo;
    setIsOnboardingShown(false);
    if (isBlank(profileName) || isBlank(profileEmail)) {
      navigation.navigate('ProfileSelection', { isUserSelfiePresent: true });
    }
  }, [navigation]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      const UserAccountInfo = getStoreRef().getState()?.UserAccountInfo;
      const userSelfie = UserAccountInfo?.selfieAnalysis?.selfie_image;
      if (isBlank(authToken) && (isPresent(userSelfie) || isPresent(userSelfieData)) && !isOnboardingShown) {
        setIsOnboardingShown(true);
        NavigationService.renderOnboarding({
          onPressOutsideAction: goBack,
          parentSkipAction: goBack,
          furtherAction: handleContinuePressed,
        });
      }
    });
    return unsubscribe;
  }, [authToken, userSelfieData, isOnboardingShown, navigation, handleContinuePressed, goBack]);

  return (
    <SafeAreaView style={styles.safeAreaViewStyle}>
      <MemoizedList
        key={listKey}
        slug={Config.FOR_YOU_TAB_SLUG}
        navigation={navigation}
        previousScreen={'ForYou'}
        customListFooter={BuildNumber}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeAreaViewStyle: {
    flex: 1,
    backgroundColor: '#f4f6f8',
  },
  scrollViewStyle: {
    backgroundColor: colors.background,
  },
});

const mapStateToProps = (state) => ({
  authToken: state.UserAccountInfo.authToken,
  userSelfieData: state.UserAccountInfo.selfieAnalysis?.my_attributes_values,
  userFacialAnalysisData: state.UserAccountInfo.facialAnalysis?.my_attributes_values,
});

export default connect(mapStateToProps, null)(memo(ForYouTabPage));

export const ForYouNavigationOptions = isDesktop()
  ? {
      headerShown: false,
    }
  : {
      headerShown: false,
      title: Config.FOR_YOU_TAB_HEADER_TITLE,
      headerTitleAlign: 'center',
      headerTitleStyle: {
        textAlign: 'center',
        alignSelf: 'center',
        fontSize: 16,
        fontFamily: 'Roboto-Medium',
      },
      headerLeft: () => <TabNavigationLeftHeader tabBar={TABBAR_CONSTANT.forYou} />,
      headerRight: () => <TabNavigationRightHeader showCart store tabBar={TABBAR_CONSTANT.forYou} />,
      headerTitle: () => {
        return (
          <Image
            source={Utility.isPresent(Config.HEADER_LOGO) ? { uri: Config.HEADER_LOGO } : images.foxy_header_image}
            style={{
              height: parseInt(Config.HEADER_LOGO_HEIGHT),
              width: parseInt(Config.HEADER_LOGO_WIDTH),
              resizeMode: 'cover',
            }}
          />
        );
      },
      headerBackVisible: false,
      headerShadowVisible: false,
      headerStyle: {
        backgroundColor: colors.white,
      },
      tabBarLabel: Config.FOR_YOU_TAB_LABEL || (() => null),
      tabBarActiveTintColor: Config.FOR_YOU_TAB_ACTIVE_COLOR,

      tabBarStyle: { borderTopWidth: 0, elevation: 0 },
      tabBarIcon: ({ focused }) => {
        return <PayoffTabIcon focused={focused} />;
      },
      tabBarOnPress: ({ navigation, defaultHandler }) => {
        defaultHandler();
        const { state: { routes = [] } = {} } = navigation;
        if (!navigation.isFocused()) return;
        if (Utility.isPresent(routes)) {
          const route = routes[0];
          const { params: { scrollToTop = () => {} } = {} } = route;
          scrollToTop();
        }
      },
    };
